/**
 * Links to one call's task, in the format the Tasks page reads
 * (?practice=&session=&task=, see its deep-link handling) and the backend builds
 * for the tracker (routers/support.py _deep_link): exactly that call's tasks,
 * that task highlighted. `session` is left out when there is none (voicemail,
 * manual task); the page then shows only that task.
 */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The in-app path, e.g. for navigate(). */
export const taskLinkPath = ({ practiceId, sessionId, taskId }) => {
  const params = new URLSearchParams({ practice: practiceId });
  if (sessionId && UUID_RE.test(sessionId)) params.set('session', sessionId);
  params.set('task', taskId);
  return `/tasks?${params.toString()}`;
};

/**
 * What the copy button under a call's conversation copies (Tasks page): when the
 * call was recorded (the conversation's created_at, left out without one), its
 * session, the caller's number and the practice, one per line. A tracker ticket
 * about the call carries the same text (trackerTicketUrl), and the tracker builds
 * it the same way for a ticket from the support chat (spcKopierblock in the
 * tracker): change them together.
 */
export const conversationCopyText = ({ recordedAt, sessionId, phoneNumber, practiceName }) => [
  recordedAt ? `Unterhaltung aufgezeichnet: ${new Date(recordedAt).toLocaleString('de-DE')}` : null,
  `Session ID: ${sessionId}`,
  phoneNumber ? `Telefonnummer: ${phoneNumber}` : null,
  practiceName ? `Praxis: ${practiceName}` : null,
].filter(Boolean).join('\n');

/** The internal tracker (DeskBot team, NetBird only). */
const TRACKER_URL = (process.env.REACT_APP_TRACKER_URL || 'http://tracker.deskbot.io').replace(/\/+$/, '');

/**
 * Where a DeskBot team member opens a tracker ticket about this call: the
 * tracker's own ticket dialog, filled in with the practice, the call time, the
 * conversation's copy text (`copyText`, see conversationCopyText) and the link
 * back to this task. Everything travels in the URL fragment, which the browser
 * never sends to a server; the tracker builds the link from the ids and looks
 * the practice up itself. Takes a Tasks page row as it is.
 */
export const trackerTicketUrl = ({ practiceId, id, sessionId, createdAt, copyText }) => {
  const params = new URLSearchParams({ app: window.location.origin, praxis: practiceId, vorgang: id });
  if (sessionId && UUID_RE.test(sessionId)) params.set('sitzung', sessionId);
  const at = createdAt ? new Date(createdAt) : null;
  if (at && !Number.isNaN(at.getTime())) params.set('zeit', at.toISOString());
  if (copyText) params.set('angaben', copyText);
  return `${TRACKER_URL}/#ticket-neu?${params.toString()}`;
};

/**
 * Opens the tracker's ticket dialog about this call (trackerTicketUrl) in a small
 * window. The window opens at once, inside the click (one opened only after a
 * request counts as a popup and is blocked), without a handle back on this app,
 * and is pointed at the tracker as soon as `loadCopyText` has the copy text
 * (without it when that fails).
 */
export const openTrackerTicketWindow = (task, loadCopyText) => {
  const features = 'popup,width=780,height=900';
  const ticketWindow = window.open('', '_blank', features);
  if (ticketWindow) ticketWindow.opener = null;
  Promise.resolve()
    .then(loadCopyText)
    .catch(() => '')
    .then((copyText) => {
      const url = trackerTicketUrl({ ...task, copyText });
      if (!ticketWindow) window.open(url, '_blank', `${features},noopener,noreferrer`);
      else if (!ticketWindow.closed) ticketWindow.location.replace(url);
    });
};
